import { useEffect, useId, useMemo, useState, useSyncExternalStore } from 'react';
import { collection, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { useAuth } from './auth-context';
import { db } from './firebase';
import { useMyNotifications } from './notifications';
import { ALERTS_WINDOW } from './familyAlerts';
import { useMyMentions, useMyThreads, useMyUnacked, type MentionHit } from './threads';
import { useUnreadHomeThreadIds } from './unreadHomeThreads';
import { BOARD_RECENT_CAP, usePersonalDiscussions } from './board';
import { discussionAudienceIncludesUser } from './discussionAudience';
import { useMessagesBoardDoor, type MessagesBoardDoorDoc } from './messagesBoardDoor';
import { useOwnSignInUids } from './ownSignIns';
import { isHiddenFixturePost } from './smokeFixture';
import type { UserDoc } from './users';
import { useAttentionBulletins } from './bulletins';
import { isNotifSnoozed, snoozeSnapshot, subscribeSnoozes } from './attentionSnooze';
import { messageInboxStateKey } from './messageInboxState';
import { classPostHandledTimes } from './familyFeedMerge';
import {
  CLASS_POST_INBOX_CAP,
  FAMILY_CLASS_POST_ACK_WINDOW,
  isClassPostRetracted,
  type ClassPostDoc,
} from './classPosts';
import {
  createWaitingFailureTracker,
  recentWaitingMentions,
  type WaitingReport,
  stillUnreadMentions,
  waitingFailureCode,
} from './staffWaitingOnYouCore';
import { captureSentryMessage } from './sentry';
import {
  messageWaitingRows,
  owedClassPostRows,
  owedClassPostsInAckWindow,
  staffAnnouncementRowUnread,
  studioAnnouncementOwesMyAck,
  withOwedClassPosts,
  type WaitingRows,
} from './messagesUnreadCore';

/**
 * The class posts an owed class post row could come from: the same
 * `expectedRecipientUids array-contains uid` query, capped at the same
 * FAMILY_CLASS_POST_ACK_WINDOW, that the family home's class post inbox and
 * the Heads up card already hold, so this is one shared listen target with
 * them (screen-read-budget.test.mjs, A-02), not a new read. Moved here from
 * familyWaitingOnYou.ts (D4 round three) when the count became one hook for
 * every sign-in (B17): a staff member who is also a parent owes her family's
 * class posts too, and Messages already shows them to her.
 *
 * Messages reads it too (B17 refute round one, finding 3), to fold the posts
 * she still owes into its own list (withOwedClassPosts): a staff member's
 * Messages reads the newest 50 posts studio-wide, so an older post sent to
 * her could be counted here with no row there. The app badge holds this same
 * query on every screen, so Messages adds no new listen target.
 */
export function useMyClassPostsForAck(uid: string | undefined): {
  posts: ClassPostDoc[];
  loading: boolean;
  error: string | null;
} {
  const [posts, setPosts] = useState<ClassPostDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!db || !uid) {
      setPosts([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    const q = query(
      collection(db, 'classPosts'),
      where('expectedRecipientUids', 'array-contains', uid),
      orderBy('createdAt', 'desc'),
      limit(FAMILY_CLASS_POST_ACK_WINDOW)
    );
    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        if (snap.metadata.fromCache || snap.metadata.hasPendingWrites) return;
        setPosts(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ClassPostDoc, 'id'>) })));
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [uid]);

  return { posts, loading, error };
}

export interface MessageWaitingOptions {
  /** Her own notifPrefs.mutedIds, passed in so this never opens its own
   *  users/{uid} listener (screen-read-budget.test.mjs). */
  mutedIds?: string[] | null;
  /** A staff sign-in: her Messages reads the studio-wide class post list
   *  and draws sub request rows. */
  staff: boolean;
  /** Her own users doc, as the caller already holds it (never a second
   *  listener). It decides what Messages decides with it: every sign-in she
   *  holds, whether staff announcement rows are drawn (staff, the Interns
   *  group, or the Jr Staff roll), and whether class post rows are drawn at
   *  all (FOH Messages has none). */
  userDoc?: (MessagesBoardDoorDoc & Partial<Pick<UserDoc, 'studentId'>>) | null;
}

export interface MessageWaitingResult {
  /** Every row Messages shows as unread, as one number. */
  count: number;
  /** What it is made of, for the home row's breakdown. */
  rows: WaitingRows;
  loading: boolean;
  /** Non-null when a check failed: the count is only what survived. */
  error: string | null;
  /** A list came back full (must-reads or announcements), so something
   *  older could sit past its edge. Not a failure; see WaitingCountState. */
  capped: boolean;
  /** The must-reads behind the count, on conversations on her list, newest
   *  first, so a home row can list them without a second listener. */
  mustReadHits: MentionHit[];
  /** Recent mentions of her still unread, on conversations on her list. */
  mentionHits: MentionHit[];
}

/**
 * THE "Waiting on you" number, for every sign-in (B17). Every row Messages
 * shows as unread: conversations (one row each, a must-read still owed counts
 * even once read), bell card rows Messages draws on their own, class posts
 * and studio announcements still owed an answer, and staff announcements
 * asking for an acknowledgement or carrying replies she has not opened. The
 * rule is lib/messagesUnreadCore.ts, which Messages itself runs to mark its
 * rows, and it is asked with what Messages asks it with: every sign-in she
 * holds, the same staff announcement door (lib/messagesBoardDoor.ts) and the
 * same class post window. To-dos and sub requests are not in it: each has
 * its own row on the home screen.
 *
 * Every listener here is one the app already holds elsewhere with the same
 * query (the bell list, the conversation list, the read cursors, the class
 * post window, the announcements, the Jr Staff roll), so Firestore serves
 * them from one target.
 */
export function useMessageWaitingRows(options: MessageWaitingOptions): MessageWaitingResult {
  const { user } = useAuth();
  const uid = user?.uid;
  const viewerDoc = options.userDoc ?? null;
  // EVERY SIGN-IN SHE HOLDS (ONE PERSON, HOWEVER SHE SIGNS IN). Messages asks
  // jrStaffAliasUidsFor over the accounts it can read; useOwnSignInUids asks
  // the same helper over the same accounts (her linked, active staff-role
  // sign-ins), with one small query only for a linked Jr Staff and none for
  // anybody else. An account that is not active gets her own uid alone, as
  // Messages does (its users list is empty for her).
  const myUids = useOwnSignInUids(uid, viewerDoc?.status === 'active' ? viewerDoc : null);
  // The same door Messages opens for staff announcement rows (finding 2 of
  // the B17 refute round: a parent on the Jr Staff roll has them too).
  const door = useMessagesBoardDoor(user, viewerDoc);
  const boardInbox = door.showBoardInbox;
  // FOH Messages draws no class post rows at all (tuckClassBoardsForRole), so
  // a card about a class post is a row of its own there and no post is owed.
  const classPostsTucked = viewerDoc?.role === 'foh';
  const {
    notifications,
    loading: notificationsLoading,
    error: notificationsError,
  } = useMyNotifications(ALERTS_WINDOW);
  const { threads, loading: threadsLoading, error: threadsError } = useMyThreads();
  const {
    threadIds: unreadThreadIds,
    unreadSinceByThreadId,
    states,
    loading: unreadLoading,
    error: unreadError,
  } = useUnreadHomeThreadIds(threads, threadsLoading, threadsError, options.mutedIds);
  // Re-asks the family must-read list every 30 seconds, as Messages does,
  // through the one shared cache every copy on the page joins.
  const {
    unacked,
    loading: unackedLoading,
    error: unackedError,
    capped: unackedCapped,
  } = useMyUnacked();
  const { hits: mentions } = useMyMentions();
  const {
    posts: classPostsForAck,
    loading: classPostsLoading,
    error: classPostsError,
  } = useMyClassPostsForAck(uid);
  const {
    bulletins: attentionBulletins,
    loading: bulletinsLoading,
    error: bulletinsError,
  } = useAttentionBulletins();
  // The identical call Messages makes, so the same list and the same targets.
  const {
    discussions,
    loading: discussionsLoading,
    error: discussionsError,
    capped: discussionsCapped,
  } = usePersonalDiscussions(
    door.showBoardInbox,
    BOARD_RECENT_CAP,
    door.onJrStaffRoll,
    door.jrStaffBoardRemoved
  );
  const participatingIds = useMemo(
    () => new Set(viewerDoc
      ? discussions
        .filter((discussion) => discussionAudienceIncludesUser(discussion, viewerDoc as UserDoc))
        .map((discussion) => discussion.id)
      : []),
    [discussions, viewerDoc]
  );

  const threadIds = useMemo(() => new Set(threads.map((thread) => thread.id)), [threads]);
  const mustReadHits = useMemo(
    () => unacked.filter((hit) => threadIds.has(hit.threadId)),
    [unacked, threadIds]
  );
  const mustReadThreadIds = useMemo(() => new Set(mustReadHits.map((hit) => hit.threadId)), [mustReadHits]);
  // A mention only names the row in the breakdown; it never adds one. Only
  // one still unread counts (stillUnreadMentions): Done, Mark read and
  // opening the conversation all clear it.
  const mentionHits = useMemo(
    () => stillUnreadMentions(recentWaitingMentions(mentions), unreadSinceByThreadId)
      .filter((hit) => threadIds.has(hit.threadId)),
    [mentions, unreadSinceByThreadId, threadIds]
  );
  const mentionThreadIds = useMemo(() => new Set(mentionHits.map((hit) => hit.threadId)), [mentionHits]);

  // THE CLASS POSTS MESSAGES DRAWS, from her own window (B17 refute round
  // one, finding 3). Messages folds every post she still owes from this
  // window into its own list (withOwedClassPosts), with the filters its own
  // read runs: a retracted post is gone, and a staff read drops the test
  // classes. A family's Messages draws her newest CLASS_POST_INBOX_CAP
  // posts, the head of this same query, and that is what decides whether a
  // card about a post folds into the post's row. A staff member's Messages
  // reads the studio-wide list, which this does not hold, so her cards are
  // taken to have their row ('every', as before). FOH Messages draws no
  // class post rows at all (tuckClassBoardsForRole): nothing is owed there
  // and every class post card is a row of its own.
  const classPostView = useMemo(() => {
    const live = classPostsForAck.filter((post) => !isClassPostRetracted(post)
      && !(options.staff && isHiddenFixturePost(post)));
    if (classPostsTucked) {
      return { owedRowPosts: [] as ClassPostDoc[], handledPosts: live, rowIds: new Set<string>() as ReadonlySet<string> };
    }
    if (options.staff) {
      return { owedRowPosts: live, handledPosts: live, rowIds: 'every' as const };
    }
    const owed = owedClassPostsInAckWindow(classPostsForAck, myUids, { isRetracted: isClassPostRetracted });
    const head = classPostsForAck
      .slice(0, CLASS_POST_INBOX_CAP)
      .filter((post) => !isClassPostRetracted(post));
    const posts = withOwedClassPosts(head, owed);
    return { owedRowPosts: posts, handledPosts: posts, rowIds: new Set(posts.map((post) => post.id)) as ReadonlySet<string> };
  }, [classPostsForAck, classPostsTucked, myUids, options.staff]);
  // Remind me later, exactly as Messages hides a row (PR #1452 review, B17):
  // the notification-kind snooze under the ROW id handleSnoozeInboxItem
  // writes, checked with the same isNotifSnoozed. snoozeStamp re-runs these
  // memos when a snooze is set or runs out (attentionSnooze.ts).
  const snoozeStamp = useSyncExternalStore(subscribeSnoozes, snoozeSnapshot, snoozeSnapshot);
  const owedClassPostIds = useMemo(
    () => owedClassPostRows(classPostView.owedRowPosts, myUids, (rowId) => isNotifSnoozed(uid, rowId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [classPostView, myUids, uid, snoozeStamp]
  );
  const owedAnnouncements = useMemo(
    () => attentionBulletins.filter((bulletin) => studioAnnouncementOwesMyAck(bulletin, myUids)).length,
    [attentionBulletins, myUids]
  );
  const staffAnnouncementRows = useMemo(
    () => (uid && boardInbox
      ? discussions.filter((discussion) => staffAnnouncementRowUnread(
        discussion,
        myUids,
        uid,
        participatingIds.has(discussion.id),
        states.get(messageInboxStateKey('board', discussion.id))
      )).length
      : 0),
    [boardInbox, discussions, myUids, participatingIds, states, uid]
  );
  // A card about a class post she already answered stops counting, from the
  // same class post list, at no extra read (familyFeedMerge.ts).
  const classPostHandledMs = useMemo(
    () => classPostHandledTimes(classPostView.handledPosts, uid ?? ''),
    [classPostView, uid]
  );

  const rows = useMemo(
    () => messageWaitingRows({
      notifications,
      threadIds,
      rows: {
        classPostIds: classPostView.rowIds,
        taskIds: 'every',
        discussionIds: boardInbox ? new Set(discussions.map((discussion) => discussion.id)) : null,
        subRequestIds: options.staff ? 'every' : null,
      },
      unreadThreadIds,
      mustReadThreadIds,
      mentionThreadIds,
      owedClassPostIds,
      owedAnnouncements,
      staffAnnouncementRows,
      isSnoozed: (cardId) => isNotifSnoozed(uid, cardId),
      handled: { classPostHandledMs },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      notifications,
      threadIds,
      classPostView,
      discussions,
      unreadThreadIds,
      mustReadThreadIds,
      mentionThreadIds,
      owedClassPostIds,
      owedAnnouncements,
      staffAnnouncementRows,
      classPostHandledMs,
      options.staff,
      boardInbox,
      uid,
      snoozeStamp,
    ]
  );

  const loading =
    notificationsLoading
    || threadsLoading
    || unreadLoading
    || unackedLoading
    || classPostsLoading
    || bulletinsLoading
    || (boardInbox && discussionsLoading);
  const error =
    notificationsError
    || threadsError
    || unreadError
    || unackedError
    || classPostsError
    || bulletinsError
    || (boardInbox ? discussionsError : null)
    || null;
  useReportWaitingFailure('alerts', notificationsError);
  useReportWaitingFailure('conversations', threadsError);
  // unreadError repeats threadsError when the conversations fail, so that
  // failure is reported once, as conversations.
  useReportWaitingFailure('read-state', threadsError ? null : unreadError);
  useReportWaitingFailure('must-reads', unackedError);
  useReportWaitingFailure('class-posts', classPostsError);
  useReportWaitingFailure('bulletins', bulletinsError);
  useReportWaitingFailure('staff-posts', boardInbox ? discussionsError : null);
  const capped = unackedCapped || (boardInbox && discussionsCapped);
  // One object per real change, so a parent handing it down (tileCounts to
  // StaffAttentionCard) does not redraw the card on every render.
  return useMemo(
    () => ({ count: rows.total, rows, loading, error, capped, mustReadHits, mentionHits }),
    [rows, loading, error, capped, mustReadHits, mentionHits]
  );
}

const waitingFailureTracker = createWaitingFailureTracker();

/**
 * Tells Sentry which check made the Waiting on you row say "Could not check
 * messages", and later how long it took to come back (Bobbie, 2026-10-26: "it
 * happened again but went away after about 5 mins"). Search Sentry for
 * waiting_source. Only the check's name and a fixed code are sent (see
 * waitingFailureCode); the person shows up through the uid and role
 * setSentryUser already put on the scope. The tracker is shared by every
 * mounted copy, so one outage sends one warning and one recovery note, and
 * the recovery waits for the last failing copy to clear.
 */
function useReportWaitingFailure(source: string, error: string | null | undefined): void {
  const code = error ? waitingFailureCode(error) : null;
  const copy = useId();
  useEffect(() => {
    sendWaitingReport(
      code
        ? waitingFailureTracker.failed(source, copy, code)
        : waitingFailureTracker.recovered(source, copy)
    );
  }, [code, copy, source]);
  // A failing copy that unmounts (Try again, or leaving Home) stops holding
  // the outage open, so the last one to go still sends the recovery note.
  useEffect(() => () => sendWaitingReport(waitingFailureTracker.unmounted(source, copy)), [copy, source]);
}

function sendWaitingReport(report: WaitingReport | null): void {
  if (!report) return;
  if (report.kind === 'failed') {
    captureSentryMessage(`waiting check failed: ${report.source} ${report.code}`, {
      level: 'warning',
      tags: { waiting_source: report.source, waiting_code: report.code },
    });
    return;
  }
  captureSentryMessage(`waiting check recovered: ${report.source} ${report.code}`, {
    level: 'info',
    tags: {
      waiting_source: report.source,
      waiting_code: report.code,
      waiting_recovered_seconds: String(report.seconds),
      waiting_recovered_how: report.how,
    },
  });
}
